'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Sun, Moon, Globe, RefreshCw, Bell } from 'lucide-react';


interface HeaderProps {
  lang: 'hi' | 'en';
  onLangToggle: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  lastUpdated?: string;
}

export default function Header({ lang, onLangToggle, onRefresh, isRefreshing, lastUpdated }: HeaderProps) {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('nj-dark-mode');
    if (stored === 'true') {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDark = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem('nj-dark-mode', String(next));
    if (next) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
      {/* Top bar — red brand strip */}
      <div className="bg-primary px-4 py-1.5 flex items-center justify-between">
        <span className="text-primary-foreground text-xs font-hindi font-medium tracking-wide">
          {lang === 'hi' ? 'झारखंड की आवाज़' : 'Voice of Jharkhand'}
        </span>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-primary-foreground/80 text-xs hidden sm:block">
              {lang === 'hi' ? 'अपडेट:' : 'Updated:'} {lastUpdated}
            </span>
          )}
          <span className="text-primary-foreground/80 text-xs">
            15 सितंबर 2026
          </span>
        </div>
      </div>

      {/* Main header */}
      <div className="px-4 py-2 flex items-center justify-between gap-3 max-w-screen-2xl mx-auto">
        {/* Logo */}
        <Link href="/" className="flex items-center shrink-0">
          <img
            src="/assets/images/image-1789459491188.png"
            alt="Namaste Jharkhand Logo"
            className="h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav links */}
        <nav className="hidden lg:flex items-center gap-1">
          {[
            { href: '/', labelHi: 'होम', labelEn: 'Home' },
            { href: '/categories-screen', labelHi: 'श्रेणियाँ', labelEn: 'Categories' },
            { href: '/news-detail-screen', labelHi: 'ताज़ा खबर', labelEn: 'Latest' },
          ].map((item) => (
            <Link
              key={`nav-${item.href}`}
              href={item.href}
              className="px-3 py-1.5 text-sm font-medium text-foreground hover:text-primary hover:bg-primary/5 rounded-lg transition-colors duration-150"
            >
              {lang === 'hi' ? item.labelHi : item.labelEn}
            </Link>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {/* Language toggle */}
          <button
            onClick={onLangToggle}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted text-sm font-medium transition-colors duration-150 btn-press"
            aria-label="Toggle language"
          >
            <Globe size={14} className="text-muted-foreground" />
            <span className="text-xs font-semibold text-foreground hidden sm:block">
              {lang === 'hi' ? 'EN' : 'हि'}
            </span>
          </button>

          {/* Refresh */}
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="p-2 rounded-lg hover:bg-muted transition-colors duration-150 btn-press disabled:opacity-50"
              aria-label="Refresh news"
            >
              <RefreshCw
                size={16}
                className={`text-muted-foreground ${isRefreshing ? 'animate-spin' : ''}`}
              />
            </button>
          )}

          {/* Notifications */}
          <button className="p-2 rounded-lg hover:bg-muted transition-colors duration-150 btn-press relative" aria-label="Notifications">
            <Bell size={16} className="text-muted-foreground" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full"></span>
          </button>

          {/* Dark mode */}
          <button
            onClick={toggleDark}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-150 btn-press"
            aria-label="Toggle dark mode"
          >
            {isDark ? (
              <Sun size={16} className="text-yellow-500" />
            ) : (
              <Moon size={16} className="text-muted-foreground" />
            )}
          </button>
        </div>
      </div>
    </header>
  );
}